export const DOCUMENT_TYPES = [
  { value: 'INSURANCE_POLICY', label: 'Insurance Policy' },
  { value: 'CERTIFICATE', label: 'Certificate of Insurance' },
  { value: 'ENDORSEMENT', label: 'Policy Endorsement' },
  { value: 'LOSS_RUN', label: 'Loss Run Report' },
  { value: 'CONTRACT', label: 'Contract' },
  { value: 'AGREEMENT', label: 'Agreement' },
  { value: 'AMENDMENT', label: 'Amendment' },
  { value: 'INSURANCE_REQ', label: 'Insurance Requirements' },
  { value: 'VENDOR_ASSESSMENT', label: 'Vendor Assessment' },
  { value: 'COMPLIANCE_DOC', label: 'Compliance Document' },
  { value: 'AUDIT_DOC', label: 'Audit Document' },
  { value: 'CLAIM_DOC', label: 'Claim Document' },
  { value: 'REPORT', label: 'Report' },
  { value: 'CORRESP', label: 'Correspondence' },
  { value: 'OTHER', label: 'Other' }
]

/** Client lifecycle stage (`setup_status` from the API). */
export const CLIENT_STAGE_LABELS = {
  active: 'Active',
  implementing: 'Implementing',
  exploring: 'Exploring',
  archived: 'Archived',
}

/** Org-profile Classify step — domain branch for modular implementation plans. */
export const ORG_KIND_OPTIONS = [
  { value: 'HOME_CARE_PROVIDER', label: 'Home care / hospice provider' },
  { value: 'HEALTHCARE_ASSOCIATION', label: 'Health care related state associations' },
  { value: 'ADVOCACY_ORGANIZATION', label: 'Advocacy organization' },
  { value: 'HEALTHCARE_PROVIDER_OTHER', label: 'Other healthcare provider' },
  { value: 'PAYOR', label: 'Payor / MCO' },
  { value: 'CONSTRUCTION', label: 'Construction company' },
  { value: 'CONSULTANT', label: 'Consultant / policy shop' },
  { value: 'INSURANCE', label: 'Insurance (non-healthcare ops)' },
  { value: 'OTHER', label: 'Other' },
]

/** Org kinds that pick service lines (what care they cover, provide or advocate for). */
export const SERVICE_LINE_ORG_KINDS = ['HEALTHCARE_ASSOCIATION', 'ADVOCACY_ORGANIZATION', 'HEALTHCARE_PROVIDER_OTHER'];

/** Sectors a health-care related state association covers (multi-select). */
export const ASSOCIATION_FOCUS_OPTIONS = [
  { value: 'Home care, home health and hospice', label: 'Home care, home health and hospice' },
  { value: 'Personal care and private duty', label: 'Personal care and private duty' },
  { value: 'Behavioral health and addiction', label: 'Behavioral health and addiction' },
  { value: 'Disability (IDD) providers', label: 'Disability (IDD) providers' },
  { value: 'Children and family agencies', label: 'Children and family agencies' },
  { value: 'Nursing homes', label: 'Nursing homes' },
  { value: 'Assisted living', label: 'Assisted living' },
  { value: 'Aging services', label: 'Aging services' },
  { value: 'Hospitals', label: 'Hospitals' },
  { value: 'Community health centers', label: 'Community health centers' },
  { value: 'Adult day and PACE', label: 'Adult day and PACE' },
  { value: 'Medical equipment, pharmacy and ambulance', label: 'Medical equipment, pharmacy and ambulance' },
]

/**
 * Landscape sections about home care agencies. Must match HOME_CARE_SECTORS in
 * backend landscape_sector_service.py (numbers carry the same labels from the API).
 */
export const LANDSCAPE_HOME_CARE_SECTORS = [
  'Home care, home health and hospice',
  'Personal care and private duty',
]

/** Must match BEHAVIORAL_SECTORS in backend landscape_sector_service.py */
export const LANDSCAPE_BEHAVIORAL_SECTORS = [
  'Behavioral health and addiction',
]

/** Must match DISABILITY_SECTORS in backend landscape_sector_service.py */
export const LANDSCAPE_DISABILITY_SECTORS = [
  'Disability (IDD) providers',
]

/** Landscape side-panel tabs about agencies (Insights is mixed and filtered card by card). */
export const LANDSCAPE_HOME_CARE_PANEL_TABS = ['plans', 'regions', 'status', 'finances']

// Frontend Performance & UX Constants
// Pilot EC2: production nginx build via docker-compose.pilot.yml (Build-5).
export const UI_CONSTANTS = {
  // Pagination & Data Loading
  DEFAULT_PAGE_SIZE: 10,
  PAGE_SIZE_OPTIONS: [5, 10, 25, 50, 100],
  MAX_INITIAL_LOAD: 50, // Max items to load initially for large datasets
  SEARCH_DEBOUNCE_MS: 300,

  // Large Dataset Thresholds
  LARGE_DATASET_THRESHOLD: 1000, // Show search/pagination when > 1000 items
  VERY_LARGE_DATASET_THRESHOLD: 10000, // Force virtualization when > 10k items

  // Split Pane Configuration
  DEFAULT_SPLIT_RATIO: 0.5, // 50/50 split
  MIN_PANEL_WIDTH: 300, // Minimum width for panels in pixels
  RESIZER_WIDTH: 8, // Width of resizer handle

  // PDF Viewer Configuration
  PDF_CONFIG: {
    toolbar: 1, // Show toolbar
    navpanes: 0, // Hide navigation panel
    scrollbar: 1, // Show scrollbar
    view: 'FitH', // Fit horizontal
    zoom: 'auto'
  },

  // Virtual Scrolling
  VIRTUAL_SCROLL_ITEM_HEIGHT: 60, // Estimated height per item
  VIRTUAL_SCROLL_BUFFER: 10, // Items to render outside viewport

  // Loading States
  SKELETON_ITEM_COUNT: 5, // Number of skeleton items to show
  LOADING_DEBOUNCE_MS: 200, // Minimum loading time to prevent flicker
}

/** Document IQ Case Review — product label (rename in one place). */
export const DOCUMENT_IQ_LABELS = {
  AUTHORIZATION_PACKET: 'Authorization packet',
}

/** Policy IQ functions sold as checkboxes. Hub tab order is separate and stays put. */
export const POLICY_IQ_NAV_MODULES = [
  { key: 'piq_ticker', label: 'Ticker', desc: 'Newsroom and RSS strip on IQ Hub.' },
  { key: 'piq_iq_hub', label: 'IQ Hub', desc: 'Daily hub and My AccessIQ.' },
  { key: 'piq_landscape', label: 'Landscape', desc: 'Regulatory landscape for this organization.' },
  { key: 'piq_state_iq', label: 'State IQ', desc: 'State legislature and agency tracking.' },
  { key: 'piq_global_iq', label: 'Global IQ', desc: 'Converging signals and global policy watch.' },
  { key: 'piq_what_if', label: 'What-If IQ', desc: 'Scenario planning and saved sessions.' },
  { key: 'piq_docket', label: 'Docket IQ', desc: 'Hearings, dockets, and calendars.' },
  { key: 'piq_pollie', label: 'Pollie Packets', desc: 'Pollie research packets.' },
  { key: 'piq_briefing', label: 'Daily Briefing', desc: 'Written briefing for this organization.' },
  { key: 'piq_artifacts', label: 'Intelligence Artifacts', desc: 'Knowledge library and artifact compare.' },
  { key: 'piq_provider_status', label: 'Provider Status', desc: 'Enrollment and provider status monitoring.' },
]

/**
 * I Wonder If customer cards use the Org Setup → Products names, in that order.
 * Must match CUSTOMER_FUNCTIONS in backend models_feedback.py.
 */
export const IWI_CUSTOMER_FUNCTIONS = [
  'Keep Me Covered',
  'Document IQ',
  ...POLICY_IQ_NAV_MODULES.flatMap((m) => {
    if (m.key === 'piq_docket') return [m.label, 'Live hearings']
    if (m.key === 'piq_briefing') return [m.label, 'Member Newsletter']
    return [m.label]
  }),
  'Advocacy IQ',
  'Community IQ',
]

export const POLICY_IQ_NAV_MODULE_KEYS = [
  ...POLICY_IQ_NAV_MODULES.map(m => m.key),
  'piq_newsletter',
]

/** Stay off when Policy IQ is turned on; must be checked on purpose. */
export const POLICY_IQ_OPT_IN_MODULE_KEYS = ['piq_newsletter', 'piq_docket']

/** Leave unchecked when Policy IQ is turned on. Existing orgs keep what they already have. */
export const POLICY_IQ_DEFAULT_OFF_MODULE_KEYS = ['piq_newsletter', 'piq_docket']

export function emptyPolicyIqProductFlags() {
  const flags = { policy_engine: false, policy_engine_live: false, piq_newsletter: false }
  POLICY_IQ_NAV_MODULES.forEach(m => { flags[m.key] = false })
  return flags
}

export function hasPolicyIqModule(features, moduleKey) {
  const f = features || {}
  if (moduleKey === 'policy_engine_live') return !!f.policy_engine_live
  if (POLICY_IQ_OPT_IN_MODULE_KEYS.includes(moduleKey)) return f[moduleKey] === true
  if (moduleKey === 'piq_ticker') {
    if (f.piq_ticker === true) return true
    if (f.piq_ticker === false) return false
    if (f.piq_iq_hub === true) return true
    if (f.piq_iq_hub === false) return false
    return !!f.policy_engine
  }
  if (f[moduleKey] === true) return true
  if (f[moduleKey] === false) return false
  return !!f.policy_engine
}

export function hasAnyPolicyIq(features) {
  const f = features || {}
  if (f.policy_engine) return true
  return POLICY_IQ_NAV_MODULE_KEYS.some(k => f[k] === true) || !!f.policy_engine_live
}

export function policyIqFlagsFromFeatures(features) {
  const f = features || {}
  const flags = emptyPolicyIqProductFlags()
  flags.policy_engine = hasAnyPolicyIq(f)
  flags.policy_engine_live = !!f.policy_engine_live
  flags.piq_newsletter = hasPolicyIqModule(f, 'piq_newsletter')
  POLICY_IQ_NAV_MODULES.forEach(m => {
    flags[m.key] = hasPolicyIqModule(f, m.key)
  })
  return flags
}

// API Response Size Limits (to prevent browser crashes)
export const API_LIMITS = {
  MAX_DOCUMENTS_PER_REQUEST: 500,
  MAX_ANALYSIS_RESULTS_PER_REQUEST: 100,
  MAX_ENTITIES_PER_REQUEST: 200,
  MAX_SEARCH_RESULTS: 1000,
}